import {
  CheckCircle2,
  CircleAlert,
  X,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Tooltip } from "@/components/ui/Tooltip";

interface ToastItem {
  id: number;
  message: string;
  tone: "success" | "error";
}

interface ToastContextValue {
  showToast: (
    message: string,
    tone?: "success" | "error",
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (message: string, tone: "success" | "error" = "success") => {
      const id = Date.now() + Math.random();
      setItems((current) => [...current, { id, message, tone }]);
      window.setTimeout(
        () =>
          setItems((current) =>
            current.filter((item) => item.id !== id),
          ),
        4500,
      );
    },
    [],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[70] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 rounded-xl border border-border bg-white p-4 shadow-floating"
          >
            {item.tone === "success" ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
            ) : (
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger-600" />
            )}
            <p className="flex-1 text-sm font-medium text-ink">
              {item.message}
            </p>
            <Tooltip label="Dismiss notification" side="left">
              <button
                onClick={() =>
                  setItems((current) =>
                    current.filter(
                      (candidate) => candidate.id !== item.id,
                    ),
                  )
                }
                className="text-ink-muted hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider.");
  }
  return context;
}
